import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail, formatCNPJ } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, PencilSimple, UserMinus } from "@phosphor-icons/react";

export default function RepresentativesPage() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", username: "", cnpj: "", codigo: "", password: "", role: "representante" });

  const load = async () => setUsers((await api.get("/users")).data);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", email: "", username: "", cnpj: "", codigo: "", password: "", role: "representante" }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name: u.name, email: u.email, username: u.username || "", cnpj: u.cnpj || "", codigo: u.codigo || "", password: "", role: u.role }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.put(`/users/${editing.id}`, payload);
      } else {
        await api.post("/users", form);
      }
      toast.success("Representante salvo."); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deactivate = async (u) => { if (!window.confirm(`Desativar ${u.name}?`)) return; await api.delete(`/users/${u.id}`); toast.success("Desativado."); load(); };

  return (
    <div className="space-y-6" data-testid="reps-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">Representantes</h1>
          <p className="text-muted-foreground mt-1">Gerencie os acessos da equipe comercial.</p>
        </div>
        <Button onClick={openNew} className="bg-accent hover:bg-accent/90 text-white gap-2" data-testid="new-rep-btn"><Plus size={18} weight="bold" /> Novo Usuário</Button>
      </div>

      <Card className="shadow-none border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-zinc-50 border-b border-border">
              {["Nome", "E-mail / Usuário", "CNPJ", "Perfil", "Status", "acoes"].map((h) => <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500">{h === "acoes" ? "" : h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-border">
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-zinc-50" data-testid={`rep-row-${u.id}`}>
                  <td className="px-4 py-3"><p className="font-medium text-zinc-900">{u.name}</p>{u.codigo ? <p className="text-xs text-zinc-400">Cód: {u.codigo}</p> : null}</td>
                  <td className="px-4 py-3 text-zinc-600">{u.email}<span className="text-zinc-400">{u.username ? ` · ${u.username}` : ""}</span></td>
                  <td className="px-4 py-3 font-mono text-zinc-600">{u.cnpj ? formatCNPJ(u.cnpj) : "—"}</td>
                  <td className="px-4 py-3"><Badge variant={u.role === "admin" ? "default" : "secondary"} className="capitalize">{u.role}</Badge></td>
                  <td className="px-4 py-3"><Badge variant="outline" className={u.active ? "border-accent text-accent" : "border-zinc-300 text-zinc-400"}>{u.active ? "Ativo" : "Inativo"}</Badge></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(u)} className="text-zinc-400 hover:text-accent mr-3" data-testid={`edit-rep-${u.id}`}><PencilSimple size={18} /></button>
                    {u.role !== "admin" && u.active && <button onClick={() => deactivate(u)} className="text-zinc-400 hover:text-destructive"><UserMinus size={18} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Editar usuário" : "Novo usuário"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="rep-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">E-mail</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="rep-email" /></div>
              <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">Usuário (login)</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="rep-username" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">CNPJ (login)</Label><Input value={formatCNPJ(form.cnpj)} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} /></div>
              <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">Código do vendedor</Label><Input value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} placeholder="Ex: 0024" data-testid="rep-codigo" /></div>
            </div>
            <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">Perfil</Label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="rep-role">
                <option value="representante">Representante</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">{editing ? "Nova senha (deixe vazio p/ manter)" : "Senha"}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="rep-password" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-accent hover:bg-accent/90 text-white" data-testid="save-rep-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
